import React from "react";
import { Tooltip, Chip, Box } from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import HourglassBottomRounded from "@mui/icons-material/HourglassBottomRounded";
import CancelRounded from "@mui/icons-material/CancelRounded";
import InfoRounded from "@mui/icons-material/InfoRounded";
import { statuses } from "../helpers";

const normalizeColor = (c) => {
  const v = String(c || "").toLowerCase();
  const map = {
    green: "success",
    red: "error",
    yellow: "warning",
    orange: "warning",
    blue: "info",
    grey: "default",
    gray: "default",
  };
  const allowed = [
    "default",
    "primary",
    "secondary",
    "success",
    "warning",
    "info",
    "error",
  ];
  const candidate = map[v] || v;
  return allowed.includes(candidate) ? candidate : "default";
};

const pickIcon = (color) => {
  switch (normalizeColor(color)) {
    case "success":
      return <CheckCircleRounded />;
    case "warning":
      return <HourglassBottomRounded />;
    case "error":
      return <CancelRounded />;
    default:
      return <InfoRounded />;
  }
};

const ProjectStatus = ({ status = {}, approvals }) => {
  const label = status.status_name || "Status";
  const description = status.description || "";
  const color = normalizeColor(status.colour || status.color);

  const chip = (
    <Chip
      component="span"
      size="small"
      icon={pickIcon(color)}
      label={label}
      sx={{
        ml: 1,
        fontWeight: 600,
        borderRadius: 2,
        // "soft" variant: subtle background + border using theme color
        bgcolor: (t) =>
          color === "default"
            ? t.palette.action.hover
            : alpha(t.palette[color].main, 0.12),
        color: (t) =>
          color === "default" ? t.palette.text.primary : t.palette[color].main,
        border: (t) =>
          `1px solid ${
            color === "default"
              ? t.palette.divider
              : alpha(t.palette[color].main, 0.4)
          }`,
        "& .MuiChip-icon": { fontSize: 18 },
        "& .MuiChip-label": { px: 1.25 },
      }}
    />
  );

  const approvedCountChip = (
    <Tooltip
      title={`Project has ${approvals.approved} ${
        approvals.approved === 1 ? "approval" : "approvals"
      }`}
    >
      <Chip
        component="span"
        size="small"
        icon={<CheckCircleRounded />}
        label={approvals.approved}
        color="success"
        sx={{
          ml: 1,
          fontWeight: 600,
          borderRadius: 2,
          "& .MuiChip-icon": { fontSize: 18 },
          "& .MuiChip-label": { px: 1.25 },
        }}
      />
    </Tooltip>
  );
  const notApprovedCountChip = (
    <Tooltip
      title={`Waiting for ${approvals.notApproved} ${
        approvals.notApproved === 1 ? "approval" : "approvals"
      }`}
    >
      <Chip
        component="span"
        size="small"
        icon={<HourglassBottomRounded />}
        label={approvals.notApproved}
        color="warning"
        sx={{
          ml: 1,
          fontWeight: 600,
          borderRadius: 2,
          "& .MuiChip-icon": { fontSize: 18 },
          "& .MuiChip-label": { px: 1.25 },
        }}
      />
    </Tooltip>
  );

  return description ? (
    <Box>
      <Tooltip title={description}>{chip}</Tooltip>
      {status.status_id === statuses.WAITING_FOR_UNIT_MANAGER_SIGNOFF && (
        <>
          {approvals.approved === 0 ? null : approvedCountChip}
          {approvals.notApproved === 0 ? null : notApprovedCountChip}
        </>
      )}
    </Box>
  ) : (
    chip
  );
};

export default ProjectStatus;
